// The generic per-instance-state capture — ported verbatim from main.js:169–191.
//
// Every renderer keeps its own instance state as directly-assigned `_`-prefixed
// properties on its module element (mod._selectedSeason, mod._mergedPlayers,
// mod._statsFilters, …). saveState() captures them ALL by that naming convention,
// never an explicit field list — an explicit list is exactly what once silently
// dropped comparison state from undo, and would need hand-editing for every future
// module that invents its own instance state (main.js:157). This generality is
// load-bearing; do not replace it with a named-field capture.
//
// A few `_`-properties are genuinely not restorable state (a live ResizeObserver a
// recreated module makes its own of; transient one-render flags) and are excluded.
//
// In Phase 03 this is a standalone pure util (built + tested now). Phase 05 calls it
// at the seam where a live module element is read into the store's `custom` bag —
// the input there is the DOM element, whose `_`-props are its instance state.

/** `_`-properties that are NOT state to restore (main.js:169). */
export const CUSTOM_STATE_DENYLIST: ReadonlySet<string> = new Set([
  "_resizeObserver", // a live ResizeObserver tied to this element; a recreated module makes its own
  "_skipBodyOpacity", // a transient one-render flag
  "_skipNextSaveState", // a transient one-shot flag consumed within the same tick
]);

/** React keeps its own bookkeeping on every element it renders (`__reactFiber$…`,
 *  `__reactProps$…`). Since 05b React renders the module element, so these start with
 *  `_` too — but they're React's, not widget state. They must never be captured: a
 *  restore would Object.assign a stale copy over the new element's own. */
const REACT_INTERNAL_PREFIX = "__react";

/**
 * Capture a module's own `_`-prefixed instance state as a serializable, deep-cloned
 * bag. Non-`_`, React-internal and denylisted keys are skipped; a value that can't survive a JSON
 * round-trip is skipped silently (main.js does this so a future module storing
 * something non-serializable can't throw inside saveState(), which runs on nearly
 * every interaction).
 *
 * The JSON round-trip both clones the value and proves it's restorable — the same
 * two-birds trick as the source.
 */
export function captureCustomState(source: Record<string, unknown>): Record<string, unknown> {
  const custom: Record<string, unknown> = {};
  for (const key in source) {
    if (key.charAt(0) !== "_" || CUSTOM_STATE_DENYLIST.has(key) || key.startsWith(REACT_INTERNAL_PREFIX)) continue;
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    try {
      custom[key] = JSON.parse(JSON.stringify(source[key]));
    } catch {
      // Skip silently — this one property just won't survive undo.
    }
  }
  return custom;
}
